import { formatLeadTime } from './calculations';

// Tipos para o relatório Enterprise
interface ProjectData {
  name: string;
  estado_atual?: any;
  estado_futuro?: any;
}

interface ReportCard {
  id: string;
  atividade?: string;
  tipo?: string;
  responsavel?: string;
  cargo?: string;
  tp?: number;
  tra?: number;
}

interface ReportMetrics {
  longestPathTP?: number;
  pce?: number;
  bottleneckName?: string;
  reworkPercentage?: number;
}

export const exportEnterpriseReport = async (
  canvasElement: HTMLElement,
  project: ProjectData,
  cards: ReportCard[],
  metrics: ReportMetrics
): Promise<void> => {
  try {
    const [{ jsPDF }, { default: autoTable }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
      import('html2canvas'),
    ]);
    // Função para capturar canvas SEM post-its
    const captureCanvas = async (element: HTMLElement) => {
      return await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        removeContainer: true,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('#canvas-content') as HTMLElement;
          if (!clonedElement) return;

          // Reset transform to get raw canvas coordinates
          clonedElement.style.transform = 'none';

          // Remover post-its
          const postits = clonedElement.querySelectorAll('[class*="postit"], [class*="post-it"], [id*="postit"], .postit, .post-it');
          postits.forEach((postit) => (postit as HTMLElement).style.display = 'none');

          // Limpar estilos
          clonedElement.style.overflow = 'visible';
          clonedElement.querySelectorAll('*').forEach((el: any) => {
            el.style.transition = 'none';
            el.style.animation = 'none';
            if (el.style.boxShadow && el.style.boxShadow !== 'none') {
              el.style.boxShadow = 'none';
            }
          });
        },
      });
    };

    // 1. Capturar mapa atual
    const canvasAtual = await captureCanvas(canvasElement);
    
    if (!canvasAtual || canvasAtual.width === 0 || canvasAtual.height === 0) {
      throw new Error('Canvas vazio ou inválido.');
    }
    
    const imgDataAtual = canvasAtual.toDataURL('image/png', 1.0);

    // Inicializar PDF (modo portrait para primeira página)
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    let currentPage = 1;
    const margin = 10;
    
    // Função para adicionar cabeçalho e rodapé
    const addPageElements = (pdfDoc: any, pageNum: number, projectData: ProjectData) => {
      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const pageHeight = pdfDoc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - 2 * margin;
      
      // CABEÇALHO PREMIUM
      pdfDoc.setFillColor(248, 250, 252);
      pdfDoc.rect(margin, margin, contentWidth, 28, 'F');
      
      pdfDoc.setFillColor(37, 99, 235);
      pdfDoc.rect(margin, margin, contentWidth, 2, 'F');
      
      pdfDoc.setDrawColor(226, 232, 240);
      pdfDoc.setLineWidth(0.3);
      pdfDoc.rect(margin, margin, contentWidth, 28);

      // LOGO WORKFLOW
      const logoX = margin + 4;
      const logoY = margin + 8;
      
      pdfDoc.setFillColor(37, 99, 235);
      pdfDoc.circle(logoX + 3, logoY + 3, 2, 'F');
      pdfDoc.setFillColor(16, 185, 129);
      pdfDoc.circle(logoX + 9, logoY + 3, 2, 'F');
      pdfDoc.setFillColor(245, 158, 11);
      pdfDoc.circle(logoX + 15, logoY + 3, 2, 'F');
      
      pdfDoc.setDrawColor(100, 116, 139);
      pdfDoc.setLineWidth(0.5);
      pdfDoc.line(logoX + 5, logoY + 3, logoX + 7, logoY + 3);
      pdfDoc.line(logoX + 11, logoY + 3, logoX + 13, logoY + 3);
      
      const textX = logoX + 20;
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.setFontSize(14);
      pdfDoc.setTextColor(31, 41, 55);
      pdfDoc.text('WORK', textX, logoY + 5);
      
      const workWidth = pdfDoc.getTextWidth('WORK');
      pdfDoc.setTextColor(37, 99, 235);
      pdfDoc.text('FLOW', textX + workWidth, logoY + 5);
      
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setFontSize(7);
      pdfDoc.setTextColor(102, 102, 102);
      pdfDoc.text('Lean Process Intelligence', textX, logoY + 9);

      // Informações do projeto
      const rightX = pageWidth - margin - 4;
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.setFontSize(11);
      pdfDoc.setTextColor(30, 41, 59);
      pdfDoc.text(projectData.name || 'Projeto Sem Título', rightX, margin + 10, { align: 'right' });
      
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(100, 116, 139);
      pdfDoc.text(`${new Date().toLocaleDateString('pt-BR')} • Relatório Enterprise`, rightX, margin + 15, { align: 'right' });
      pdfDoc.text(`v2.1.0 • Engine Kaizen PROD`, rightX, margin + 20, { align: 'right' });

      // RODAPÉ
      const footerY = pageHeight - margin - 3;
      
      pdfDoc.setDrawColor(226, 232, 240);
      pdfDoc.setLineWidth(0.3);
      pdfDoc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(100, 116, 139);
      pdfDoc.text(`Página ${pageNum}`, margin, footerY);
      
      pdfDoc.setFontSize(7);
      pdfDoc.setTextColor(120, 120, 120);
      pdfDoc.text('Documento confidencial • Análise de processo sujeita a validação em Gemba', pageWidth / 2, footerY, { align: 'center' });
      
      pdfDoc.setFontSize(7);
      pdfDoc.setTextColor(100, 116, 139);
      pdfDoc.text('Gerado via Workflow', pageWidth - margin, footerY, { align: 'right' });
    };

    // PÁGINA 1: KPI Dashboard
    addPageElements(pdf, currentPage, project);
    
    const kpiStartY = margin + 35;
    const kpiCardHeight = 28;
    const kpiGap = 4;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - 2 * margin;
    const kpiCardWidth = (contentWidth - 3 * kpiGap) / 4;
    
    const drawKpiCard = (x: number, y: number, label: string, value: string, color: [number, number, number]) => {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(x, y, kpiCardWidth, kpiCardHeight, 'F');
      
      pdf.setFillColor(color[0], color[1], color[2]);
      pdf.rect(x, y, kpiCardWidth, 2, 'F');
      
      pdf.setDrawColor(226, 232, 240);
      pdf.setLineWidth(0.3);
      pdf.rect(x, y, kpiCardWidth, kpiCardHeight);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(100, 116, 139);
      pdf.text(label, x + kpiCardWidth / 2, y + 8, { align: 'center' });
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(30, 41, 59);
      pdf.text(value, x + kpiCardWidth / 2, y + 18, { align: 'center' });
    };

    const leadTimeFormatted = formatLeadTime(metrics.longestPathTP || 0);
    const pce = metrics.pce ? `${(metrics.pce * 100).toFixed(1)}%` : 'N/A';
    const bottleneck = metrics.bottleneckName || 'N/A';
    const rework = metrics.reworkPercentage ? `${(metrics.reworkPercentage * 100).toFixed(1)}%` : 'N/A';

    const colorBlue: [number, number, number] = [37, 99, 235];
    const colorGreen: [number, number, number] = [16, 185, 129];
    const colorAmber: [number, number, number] = [245, 158, 11];
    const colorRed: [number, number, number] = [239, 68, 68];

    drawKpiCard(margin, kpiStartY, 'LEAD TIME TOTAL', leadTimeFormatted, colorBlue);
    drawKpiCard(margin + kpiCardWidth + kpiGap, kpiStartY, 'PCE (EFICIÊNCIA)', pce, colorGreen);
    drawKpiCard(margin + 2 * (kpiCardWidth + kpiGap), kpiStartY, 'GARGALO', bottleneck, colorAmber);
    drawKpiCard(margin + 3 * (kpiCardWidth + kpiGap), kpiStartY, 'RETRABALHO', rework, colorRed);

    // PÁGINA 2: Mapa Atual (A4 Paisagem)
    pdf.addPage('a4', 'landscape');
    currentPage++;
    
    const landscapeWidth = pdf.internal.pageSize.getWidth();
    const landscapeHeight = pdf.internal.pageSize.getHeight();
    
    // Título centralizado
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(30, 41, 59);
    pdf.text('MAPA DO ESTADO ATUAL', landscapeWidth / 2, 15, { align: 'center' });
    
    // Calcular dimensões para ocupar toda a folha
    const pxToMm = 25.4 / 96;
    const imgWidthMm = canvasAtual.width * pxToMm;
    const imgHeightMm = canvasAtual.height * pxToMm;
    
    const availableWidth = landscapeWidth - 2 * margin;
    const availableHeight = landscapeHeight - 30;
    
    const ratio = Math.min(availableWidth / imgWidthMm, availableHeight / imgHeightMm);
    const finalWidth = imgWidthMm * ratio;
    const finalHeight = imgHeightMm * ratio;
    
    const xPos = (landscapeWidth - finalWidth) / 2;
    const yPos = 20;
    
    pdf.addImage(imgDataAtual, 'PNG', xPos, yPos, finalWidth, finalHeight, undefined, 'FAST');

    // PÁGINA 3: Tabela Técnica
    pdf.addPage();
    currentPage++;
    addPageElements(pdf, currentPage, project);

    const titleY = margin + 36;
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, titleY, contentWidth, 10, 'F');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(30, 41, 59);
    pdf.text('Detalhamento Técnico do Processo', margin + 3, titleY + 7);
    
    pdf.setDrawColor(37, 99, 235);
    pdf.setLineWidth(0.5);
    pdf.line(margin, titleY + 11, margin + 40, titleY + 11);

    const tableHeaders = ['ID', 'Nome da Etapa', 'Tipo', 'Responsável', 'Lead Time (min)', 'Tempo de Realização'];
    const tableData = cards.map(card => [
      card.id.toString(),
      card.atividade || '-',
      card.tipo || '-',
      card.cargo || card.responsavel || '-',
      (card.tp || 0).toFixed(0),
      (card.tra || 0).toFixed(0),
    ]);

    autoTable(pdf, {
      head: [tableHeaders],
      body: tableData,
      startY: titleY + 15,
      margin: { left: margin, right: margin },
      styles: { 
        fontSize: 8, 
        cellPadding: 3, 
        valign: 'middle',
        halign: 'center',
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        textColor: [51, 65, 85]
      },
      headStyles: { 
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 8,
        cellPadding: 4
      },
      alternateRowStyles: { 
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
        1: { halign: 'left', cellWidth: 'auto' },
        2: { halign: 'center', cellWidth: 25 },
        3: { halign: 'left', cellWidth: 35 },
        4: { halign: 'center', cellWidth: 25 },
        5: { halign: 'center', cellWidth: 30 },
      },
      theme: 'grid',
      didDrawPage: (data: any) => {
        if (data.pageNumber > currentPage) {
          currentPage = data.pageNumber;
          addPageElements(pdf, currentPage, project);
        }
      }
    });

    // Salvar PDF
    const fileName = `${project.name.replace(/[^a-z0-9]/gi, '_')}_Enterprise_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

  } catch (error) {
    throw new Error('Falha ao exportar relatório Enterprise. Tente novamente.');
  }
};
